import React from "react";
import { ButtonDS, GlobalIcon } from "get-life-storybook-ts";
import V3PriceDisplay from "./V3PriceDisplay";
import { DownloadableFilesT } from "get-life-storybook-ts/lib/components/DownloadableFiles/DownloadableFiles";

export interface V3CoverageBreakdownLine {
  label: string;
  value: string;
}

interface V3PriceCardProps {
  translate: (key: string) => string;
  amount: number;
  loading: boolean;
  ctaLabel: string;
  onCtaClick: () => void;
  breakdown: V3CoverageBreakdownLine[];
  startsLabel?: string;
  draftId?: string;
  downloadableFiles?: DownloadableFilesT;
  downloadDocument?: (type: string) => void;
  beforePromoText?: string;
  afterPromoText?: string;
  onPromoClick?: () => void;
}

const V3PriceCard: React.FC<V3PriceCardProps> = ({
  translate,
  amount,
  loading,
  ctaLabel,
  onCtaClick,
  breakdown,
  startsLabel,
  draftId,
  downloadableFiles,
  downloadDocument,
  beforePromoText,
  afterPromoText,
  onPromoClick,
}) => {
  const generalConditions = downloadableFiles?.find(
    (f) => f?.type === "offer-w-base" || f?.type === "offer-multi"
  );
  const legalNote = downloadableFiles?.find((f) => f?.type === "legal-note");

  return (
    <div className="upsell-v3__price-card">
      <span className="upsell-v3__price-card-badge">
        {translate("newUpsell.v3.priceCard.offerBadge")}
      </span>
      <div className="upsell-v3__price-card-priceWrap">
        <span className="upsell-v3__price-card-priceLabel">
          {translate("newUpsell.v3.priceCard.priceLabel")}
        </span>
        {beforePromoText && (
          <p
            className="upsell-v3__price-card-before"
            dangerouslySetInnerHTML={{ __html: beforePromoText }}
          />
        )}
        <V3PriceDisplay
          amount={amount}
          loading={loading}
          size={112}
          suffix="/mes"
        />
        {afterPromoText && (
          <p className="upsell-v3__price-card-after">
            <span dangerouslySetInnerHTML={{ __html: afterPromoText }} />
            {onPromoClick && (
              <span
                role="button"
                tabIndex={0}
                onClick={onPromoClick}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    onPromoClick();
                  }
                }}
                className="upsell-v3__price-card-after-info"
              >
                <GlobalIcon iconName="InfoIcon" size="XS" color="#7B7B7B" />
              </span>
            )}
          </p>
        )}
      </div>

      {breakdown.length > 0 && (
        <div className="upsell-v3__price-card-summary">
          {breakdown.map((line) => (
            <div className="upsell-v3__price-card-summaryRow" key={line.label}>
              <span className="label">{line.label}</span>
              <span className="value">{line.value}</span>
            </div>
          ))}
          {startsLabel && (
            <div className="upsell-v3__price-card-summaryRow upsell-v3__price-card-summaryRow--total">
              <span className="label">
                {translate("newUpsell.v3.priceCard.startsLabel")}
              </span>
              <span className="value">{startsLabel}</span>
            </div>
          )}
        </div>
      )}

      <div className="upsell-v3__price-card-cta">
        <ButtonDS
          label={ctaLabel}
          onClick={onCtaClick}
          rightIcon="ArrowRightIcon"
          className="w-full"
        />
      </div>
      <div className="upsell-v3__price-card-trust">
        <span>
          <GlobalIcon iconName="LockIcon" size="XS" color="#8E8E8E" />
          {translate("newUpsell.v3.priceCard.security")}
        </span>
        <span className="sep">·</span>
        <span>{translate("newUpsell.v3.priceCard.paySigned")}</span>
        <span className="sep">·</span>
        <span>{translate("newUpsell.v3.priceCard.cancelFree")}</span>
      </div>
      {(generalConditions || legalNote) && (
        <div className="upsell-v3__price-card-docs">
          {generalConditions && (
            <button
              type="button"
              onClick={() =>
                downloadDocument?.(generalConditions.type as string)
              }
            >
              <GlobalIcon iconName="FileTextIcon" size="XS" color="#686868" />
              {translate("newUpsell.v3.priceCard.docConditions")}
            </button>
          )}
          {legalNote && (
            <button
              type="button"
              onClick={() => downloadDocument?.(legalNote.type as string)}
            >
              <GlobalIcon iconName="FileTextIcon" size="XS" color="#686868" />
              {translate("newUpsell.v3.priceCard.docPrev")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default V3PriceCard;
